/**
 * manual-quote-helpers.ts — Kiểu dữ liệu + hàm thuần cho Platform → Báo giá thủ công.
 * Backend: ecdp_backend_source/src/modules/platform/manual-quote/* — server luôn tính tiền (POST preview),
 * giao diện chỉ gửi đầu vào và hiển thị kết quả. moneyWords port từ docs/mau-bieu/don-dat-hang-thu-cong.html
 * (trùng hàm backend manual-quote-calc.ts — test cùng bộ số).
 */
import type { PricingConfig, PricingPlan, Quotas } from '../pricing-editor'

export type OrderType = 'new' | 'renew' | 'upgrade' | 'addon'
export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  new: 'Đăng ký mới', renew: 'Gia hạn', upgrade: 'Nâng cấp gói', addon: 'Mua thêm hạn mức',
}

export type QuoteStatus = 'draft' | 'sent' | 'paid' | 'cancelled' | 'expired'
export const STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: 'Nháp', sent: 'Đã gửi', paid: 'Đã thanh toán', cancelled: 'Đã huỷ', expired: 'Quá hạn',
}
export const STATUS_VARIANT: Record<QuoteStatus, 'secondary' | 'default' | 'success' | 'outline' | 'warning'> = {
  draft: 'secondary', sent: 'default', paid: 'success', cancelled: 'outline', expired: 'warning',
}

export const MODE_LABELS: Record<string, string> = {
  new: 'Mua mới — kỳ tính từ lúc xác nhận thanh toán',
  extend: 'Gia hạn nối tiếp từ ngày hết hạn hiện tại',
  requantity: 'Đổi số lượng cùng gói — quy đổi phần còn lại, kỳ mới từ lúc thanh toán',
  change_plan: 'Nâng gói — quy đổi phần còn lại, kỳ mới từ lúc thanh toán',
  addon: 'Mua thêm cho phần còn lại của kỳ hiện tại',
}

/** Mã lỗi chính sách mua mà "ghi đè" bỏ qua được. */
export const POLICY_CODES = new Set(['DOWNGRADE_NOT_ALLOWED', 'QUANTITY_DECREASE_NOT_ALLOWED', 'QUANTITY_BELOW_USAGE'])

export interface Customer {
  name: string
  taxCode: string
  representative: string
  address: string
  contact: string
  phone: string
  email: string
}

export interface ExtraLine {
  name: string
  quantity: string
  unitPrice: string
}

export interface QuoteForm {
  orgId: string | null
  orgName: string
  customer: Customer
  orderType: OrderType
  planId: string
  days: string
  startDate: string
  quantities: Record<keyof Quotas, string>
  promoCode: string
  extraDiscount: string
  extraDiscountReason: string
  extras: ExtraLine[]
  override: boolean
  overrideReason: string
  note: string
  dueDate: string
  terms: string
}

export interface Template {
  companyName: string
  address: string
  taxCode: string
  phone: string
  email: string
  website: string
  logoUrl: string
  signerName: string
  bankName: string
  bankAccount: string
  bankHolder: string
  bankBin: string
  paymentMethod: string
  terms: string
  dueDays: number
}

export interface BreakdownItem { label: string; description: string; quantity: number; unitPrice: number; amount: number }
export interface Extras {
  lines: { name: string; quantity: number; unitPrice: number; amount: number }[]
  preTax: number
  vatPercent: number
  vatAmount: number
  total: number
}

export interface Breakdown {
  orderType: OrderType
  mode: string
  plan: { id: string; name: string; included: Quotas; activeContactsPerMonth: number | null; aiRepliesPerDay: number | null }
  items: BreakdownItem[]
  perMonth: number
  days: number
  subtotal: number
  discountPercent: number
  discount: number
  promo: { code: string; amount: number; stacked: boolean } | null
  promoError: string | null
  promoNote: string | null
  grossTotal: number
  credit: { amount: number; applied: number; fromPlanName: string; remainingDays: number } | null
  extraDays: number
  totalDays: number
  manualDiscount: { amount: number; reason: string } | null
  planPreTax: number
  planVat: { percent: number; preTax: number; amount: number }
  planTotal: number
  extras: Extras
  preTaxTotal: number
  vatPercent: number
  vatTotal: number
  grandTotal: number
  words: string
  addon: { from: Quotas; delta: Quotas; periodEnd: string; remainingDays: number } | null
  override: { reason: string } | null
  warnings: string[]
  pricingUpdatedAt?: string | null
}

export interface ManualQuote {
  id: string
  number: string
  orgId: string | null
  customer: Customer
  seller: Template
  input: Record<string, unknown> & { orderType: OrderType; startDate: string | null; terms?: string; note?: string }
  breakdown: Breakdown
  total: number
  status: QuoteStatus
  displayStatus: QuoteStatus
  dueDate: string | null
  note: string
  createdBy: string
  createdByName?: string | null
  paidAt: string | null
  paymentRef: string | null
  billingOrderId: string | null
  orgCreated: boolean
  sentAt: string | null
  cancelledAt: string | null
  cancelReason: string | null
  createdAt: string
  updatedAt: string
  org?: { id: string; name: string; plan: string | null; expiresAt: string | null } | null
  order?: { id: string; status: string; amount: number; provider: string; paidAt: string | null; periodStart: string | null; periodEnd: string | null; invoiceStatus: string } | null
}

export interface ManualQuoteListItem {
  id: string
  number: string
  orgId: string | null
  customerName: string
  orderType: OrderType | null
  planName: string | null
  days: number | null
  total: number
  status: QuoteStatus
  displayStatus: QuoteStatus
  dueDate: string | null
  billingOrderId: string | null
  createdByName: string | null
  createdAt: string
}

export interface OrgContext {
  org: { id: string; name: string; ownerEmail: string | null; ownerName: string | null }
  plan: string | null
  effectivePlan: string | null
  planName: string | null
  expiresAt: string | null
  status: string | null
  purchased: Quotas | null
  usage: Quotas | null
  upgradeOnly: boolean
  suggestedType: OrderType
}

export const EMPTY_CUSTOMER: Customer = { name: '', taxCode: '', representative: '', address: '', contact: '', phone: '', email: '' }

/** YYYY-MM-DD theo giờ máy (ngày làm việc của người lập). */
export function isoDate(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00`)
  d.setDate(d.getDate() + days)
  return isoDate(d)
}

/** "2026-09-29" → "29/09/2026". */
export function viDate(s: string | null | undefined): string {
  if (!s) return ''
  const [y, m, d] = s.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

/** Ngày cuối kỳ (gồm ngày bắt đầu): 30 ngày từ 01/10 → 30/10. */
export function endDate(start: string, days: number): string {
  if (!start || !(days > 0)) return ''
  return addDays(start, days - 1)
}

const toStr = (n: number) => String(n)

export function quantitiesOf(q: Quotas): QuoteForm['quantities'] {
  return { members: toStr(q.members), officialChannels: toStr(q.officialChannels), personalChannels: toStr(q.personalChannels) }
}

/** Gói bán được (khớp computeQuote: có giá, purchasable, không ẩn). */
export function sellablePlans(cfg: PricingConfig | undefined): PricingPlan[] {
  return (cfg?.plans ?? []).filter((p) => p.purchasable && !p.hidden && p.monthlyPrice != null && p.monthlyPrice > 0)
}

/** Form báo giá mới: gói nổi bật (hoặc gói bán được đầu tiên), số ngày mặc định của bảng giá, số lượng gồm sẵn. */
export function emptyForm(cfg: PricingConfig | undefined, template: Template | undefined, today: Date = new Date()): QuoteForm {
  const plans = sellablePlans(cfg)
  const plan = plans.find((p) => p.highlight) ?? plans[0]
  const start = isoDate(today)
  return {
    orgId: null,
    orgName: '',
    customer: { ...EMPTY_CUSTOMER },
    orderType: 'new',
    planId: plan?.id ?? '',
    days: String(cfg?.defaultDays ?? 30),
    startDate: start,
    quantities: quantitiesOf(plan?.included ?? { members: 0, officialChannels: 0, personalChannels: 0 }),
    promoCode: '',
    extraDiscount: '',
    extraDiscountReason: '',
    extras: [],
    override: false,
    overrideReason: '',
    note: '',
    dueDate: addDays(start, template?.dueDays ?? 7),
    terms: '',
  }
}

/** Đổi gói: số lượng không thấp hơn số gồm sẵn của gói mới. */
export function applyPlanDefaults(form: QuoteForm, plan: PricingPlan | undefined): QuoteForm {
  if (!plan) return form
  const q = { ...form.quantities }
  for (const k of Object.keys(q) as (keyof Quotas)[]) {
    if ((Number(q[k]) || 0) < plan.included[k]) q[k] = String(plan.included[k])
  }
  return { ...form, planId: plan.id, quantities: q }
}

/** Form → body gửi API (preview / lưu). Ô số rỗng → 0 để server báo lỗi rõ ràng. */
export function formToInput(f: QuoteForm) {
  const num = (s: string) => (s.trim() === '' ? 0 : Number(s.replace(/[^\d-]/g, '')))
  return {
    orgId: f.orgId,
    customer: f.customer,
    orderType: f.orderType,
    planId: f.planId,
    days: f.orderType === 'addon' ? undefined : num(f.days),
    startDate: f.startDate || null,
    quantities: {
      members: num(f.quantities.members), officialChannels: num(f.quantities.officialChannels), personalChannels: num(f.quantities.personalChannels),
    },
    promoCode: f.promoCode.trim() || null,
    extraDiscount: f.extraDiscount.trim() ? { amount: num(f.extraDiscount), reason: f.extraDiscountReason } : null,
    extras: f.extras.map((e) => ({ name: e.name, quantity: num(e.quantity), unitPrice: num(e.unitPrice) })),
    override: f.override ? { enabled: true, reason: f.overrideReason } : null,
    note: f.note,
    dueDate: f.dueDate || null,
    terms: f.terms,
  }
}

/** Báo giá đã lưu → form sửa. */
export function quoteToForm(q: ManualQuote): QuoteForm {
  const i = q.input as Record<string, any>
  const ed = i.extraDiscount as { amount: number; reason: string } | null
  const ov = i.override as { reason: string } | null
  return {
    orgId: q.orgId,
    orgName: q.org?.name ?? '',
    customer: { ...EMPTY_CUSTOMER, ...(q.customer ?? {}) },
    orderType: (i.orderType as OrderType) ?? 'new',
    planId: String(i.planId ?? q.breakdown?.plan?.id ?? ''),
    days: String(i.days || q.breakdown?.days || 30),
    startDate: String(i.startDate ?? ''),
    quantities: quantitiesOf((i.quantities as Quotas) ?? { members: 0, officialChannels: 0, personalChannels: 0 }),
    promoCode: String(i.promoCode ?? ''),
    extraDiscount: ed ? String(ed.amount) : '',
    extraDiscountReason: ed?.reason ?? '',
    extras: ((i.extras as { name: string; quantity: number; unitPrice: number }[]) ?? []).map((e) => ({ name: e.name, quantity: String(e.quantity), unitPrice: String(e.unitPrice) })),
    override: !!ov,
    overrideReason: ov?.reason ?? '',
    note: q.note ?? '',
    dueDate: q.dueDate ?? '',
    terms: String(i.terms ?? ''),
  }
}

/** Gợi ý số ngày = các mốc thời hạn của bảng giá (kèm % giảm). */
export function daySuggestions(cfg: PricingConfig | undefined): { days: number; label: string }[] {
  return (cfg?.durations ?? []).map((d) => ({ days: d.days, label: `${d.days} ngày${d.discountPercent ? ` (−${d.discountPercent}%)` : ''}` }))
}

/** Điều khoản: mỗi dòng một điều (bỏ dòng trống). */
export function termLines(terms: string | null | undefined): string[] {
  return (terms ?? '').split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
}

/** Nội dung chuyển khoản = số báo giá bỏ ký tự đặc biệt (giống mẫu HTML). */
export function transferNote(number: string): string {
  return number.replace(/[^A-Za-z0-9]/g, '') || 'BAOGIA'
}

/** Ảnh mã VietQR (img.vietqr.io) chứa sẵn số tiền + nội dung. null khi thiếu ngân hàng / số TK / số tiền. */
export function vietQrUrl(
  bank: { bankBin: string; bankAccount: string; bankHolder: string },
  amount: number,
  note: string,
  style: 'qr_only' | 'compact2' = 'qr_only',
): string | null {
  const bin = (bank.bankBin || '').trim()
  const acc = (bank.bankAccount || '').replace(/\s/g, '')
  if (!bin || !acc || !(amount > 0)) return null
  const q = `?amount=${Math.round(amount)}&addInfo=${encodeURIComponent(note)}&accountName=${encodeURIComponent(bank.bankHolder || '')}`
  return `https://img.vietqr.io/image/${encodeURIComponent(bin)}-${encodeURIComponent(acc)}-${style}.png${q}`
}

// ── Số tiền bằng chữ (port nguyên hàm moneyWords của mẫu HTML) ───────

const DIGITS = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín']

function readTriple(n: number, full: boolean): string {
  const h = Math.floor(n / 100)
  const t = Math.floor((n % 100) / 10)
  const u = n % 10
  const out: string[] = []
  if (full || h) out.push(`${DIGITS[h]} trăm`)
  if (t > 1) {
    out.push(`${DIGITS[t]} mươi`)
    if (u === 1) out.push('mốt')
    else if (u === 4) out.push('tư')
    else if (u === 5) out.push('lăm')
    else if (u) out.push(DIGITS[u])
  } else if (t === 1) {
    out.push('mười')
    if (u === 5) out.push('lăm')
    else if (u) out.push(DIGITS[u])
  } else if (u) {
    if (full || h) out.push('lẻ')
    out.push(DIGITS[u])
  }
  return out.join(' ')
}

export function moneyWords(value: number): string {
  let n = Math.round(Number(value) || 0)
  if (n <= 0) return 'Không đồng'
  const scales = ['', 'nghìn', 'triệu', 'tỷ', 'nghìn tỷ', 'triệu tỷ']
  const groups: number[] = []
  while (n > 0) {
    groups.push(n % 1000)
    n = Math.floor(n / 1000)
  }
  const parts: string[] = []
  for (let i = groups.length - 1; i >= 0; i--) {
    if (!groups[i]) continue
    parts.push(readTriple(groups[i], i < groups.length - 1) + (scales[i] ? ` ${scales[i]}` : ''))
  }
  const s = `${parts.join(' ')} đồng`
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Số tiền dạng 1.490.000 (không ký hiệu). */
export function fmt(n: number | null | undefined): string {
  return Math.round(Number(n) || 0).toLocaleString('vi-VN')
}

/** Mã chống gửi trùng cho mỗi lần mở hộp thoại xác nhận thanh toán. */
export function newIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `mq-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}
